// client/src/widgets/DebugPanel/DebugPanel.tsx
// Panneau debug docke en bas — pousse l'app vers le haut (non-overlay), toggle Ctrl+Shift+D
// Le buffer d'events (onglet Stream) n'est capture que si localStorage.MB_DEBUG === 'true'

import { useState, useEffect, useCallback, useRef } from 'react'
import { getDebugBuffer, clearDebugBuffer, isDebugMode, type DebugEvent, type DebugSource } from '@/shared/lib/debug'
import { queryClient } from '@/shared/lib/query-client'
import { useMessagesStore } from '@/shared/stores/messages.store'
import { useStatusStore } from '@/shared/stores/status.store'
import { useMetricsStore } from '@/shared/stores/metrics.store'
import { useTerminalStore } from '@/shared/stores/terminal.store'
import { useUIStore } from '@/shared/stores/ui.store'
import { useTabsStore } from '@/shared/stores/tabs.store'
import { useAuthStore } from '@/shared/stores/auth.store'
import { trpc } from '@/shared/lib/trpc'
import { useDebugPanelStore } from '@/shared/stores/debug-panel.store'

// --- Types internes ---

type Tab = 'stream' | 'cache' | 'stores' | 'daemon'

const SOURCE_COLORS: Record<DebugSource, string> = {
  WS: '#2196F3',
  Bus: '#9C27B0',
  RQ: '#4CAF50',
  Zustand: '#FF9800',
  Mutation: '#F44336',
  Error: '#FF0000',
}

const ALL_SOURCES: DebugSource[] = ['WS', 'Bus', 'RQ', 'Zustand', 'Mutation', 'Error']

// Plafond de longueur d'un payload dans le texte copié (une ligne par event).
// Les events WS `messages`/RQ portent l'enveloppe complète du tour (contenu de la
// conversation, des dizaines de Ko après un /mother) : sans plafond, le Copy vire au
// dump de conversation. On garde le signal (labels, petits payloads : sessionId,
// status, tailles) et on tronque les blobs avec un marqueur explicite.
const COPY_PAYLOAD_MAX = 200

/** Sérialise un payload en une seule ligne, plafonnée à COPY_PAYLOAD_MAX. */
function compactPayload(payload: unknown): string {
  if (payload === undefined) return ''
  let s: string
  try {
    // JSON.stringify peut rendre undefined à l'exécution (fonction/symbole en racine) —
    // repli sur String(). Événements debug = données planes, mais on reste défensif.
    s = JSON.stringify(payload) ?? String(payload)
  } catch {
    s = String(payload)
  }
  if (s.length > COPY_PAYLOAD_MAX) {
    return `${s.slice(0, COPY_PAYLOAD_MAX)}… (+${s.length - COPY_PAYLOAD_MAX} chars)`
  }
  return s
}

// --- Sous-composant Stream Tab ---

function StreamTab() {
  const [events, setEvents] = useState<DebugEvent[]>([])
  const [activeFilters, setActiveFilters] = useState<Set<DebugSource>>(new Set(ALL_SOURCES))
  const [autoScroll, setAutoScroll] = useState(true)
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'ok' | 'error'>('idle')
  // Index (dans la liste filtree affichee) des events depliés — payload JSON complet visible
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set())
  const listRef = useRef<HTMLDivElement>(null)

  // Rafraîchit le buffer toutes les 200ms
  useEffect(() => {
    const interval = setInterval(() => {
      setEvents(getDebugBuffer())
    }, 200)
    return () => clearInterval(interval)
  }, [])

  // Auto-scroll en bas
  useEffect(() => {
    if (autoScroll && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [events, autoScroll])

  const toggleFilter = (source: DebugSource) => {
    setActiveFilters(prev => {
      const next = new Set(prev)
      if (next.has(source)) {
        next.delete(source)
      } else {
        next.add(source)
      }
      return next
    })
  }

  const handleClear = () => {
    clearDebugBuffer()
    setEvents([])
    setExpandedIndices(new Set())
  }

  const toggleExpand = (i: number) => {
    setExpandedIndices(prev => {
      const next = new Set(prev)
      if (next.has(i)) {
        next.delete(i)
      } else {
        next.add(i)
      }
      return next
    })
  }

  // Copie UNIQUEMENT le contenu du panneau DBG : les events du Stream actuellement
  // affichés (filtres actifs respectés), une ligne par event avec payload compact.
  // Jamais un dump de la conversation — cf. compactPayload / COPY_PAYLOAD_MAX.
  const handleCopy = () => {
    const text = events
      .filter(e => activeFilters.has(e.source))
      .map(e => {
        const p = compactPayload(e.payload)
        return `[${e.ts}] [${e.source}] ${e.label}${p ? ' ' + p : ''}`
      })
      .join('\n')
    void navigator.clipboard.writeText(text)
  }

  // Sync le buffer debug vers /tmp/mb-debug-client.json (lisible par Claude)
  const handleSync = useCallback(async () => {
    const token = useAuthStore.getState().accessToken
    if (!token) return
    setSyncStatus('syncing')
    try {
      const buffer = getDebugBuffer()
      const res = await fetch('/api/debug/client-buffer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          events: buffer,
          syncedAt: new Date().toISOString(),
          source: 'client',
        }),
      })
      setSyncStatus(res.ok ? 'ok' : 'error')
    } catch {
      setSyncStatus('error')
    }
    // Reset status après 2s
    setTimeout(() => setSyncStatus('idle'), 2000)
  }, [])

  // Auto-sync toutes les 30s si debug actif
  useEffect(() => {
    if (!isDebugMode()) return
    const interval = setInterval(() => {
      void handleSync()
    }, 30_000)
    return () => clearInterval(interval)
  }, [handleSync])

  const filtered = events.filter(e => activeFilters.has(e.source))
  const expandableCount = filtered.reduce((n, e) => n + (e.payload !== undefined ? 1 : 0), 0)
  const allExpanded = expandableCount > 0 && expandedIndices.size >= expandableCount

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedIndices(new Set())
    } else {
      setExpandedIndices(new Set(filtered.reduce<number[]>((acc, e, i) => {
        if (e.payload !== undefined) acc.push(i)
        return acc
      }, [])))
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Filtres sources */}
      <div style={{ display: 'flex', gap: 6, padding: '4px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        {ALL_SOURCES.map(src => (
          <label
            key={src}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              cursor: 'pointer',
              fontSize: 11,
              color: activeFilters.has(src) ? SOURCE_COLORS[src] : '#666',
              fontWeight: activeFilters.has(src) ? 700 : 400,
            }}
          >
            <input
              type="checkbox"
              checked={activeFilters.has(src)}
              onChange={() => toggleFilter(src)}
              style={{ width: 10, height: 10, accentColor: SOURCE_COLORS[src] }}
            />
            {src}
          </label>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#aaa', cursor: 'pointer' }}>
            <input type="checkbox" checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} style={{ width: 10, height: 10 }} />
            auto
          </label>
          <button
            onClick={toggleExpandAll}
            disabled={expandableCount === 0}
            style={{ ...btnStyle, cursor: expandableCount === 0 ? 'default' : 'pointer', opacity: expandableCount === 0 ? 0.4 : 1 }}
            title={allExpanded ? 'Replier tous les events' : 'Deplier tous les events (payload JSON complet)'}
          >
            {allExpanded ? 'Replier tout' : 'Deplier tout'}
          </button>
          <button onClick={handleCopy} style={btnStyle}>Copy</button>
          <button onClick={handleClear} style={btnStyle}>Clear</button>
          <button
            onClick={() => { void handleSync() }}
            disabled={syncStatus === 'syncing'}
            style={{
              ...btnStyle,
              color: syncStatus === 'ok' ? '#4CAF50' : syncStatus === 'error' ? '#F44336' : '#ccc',
              cursor: syncStatus === 'syncing' ? 'wait' : 'pointer',
            }}
            title="Sync buffer vers /tmp/mb-debug-client.json (lisible par Claude)"
          >
            {syncStatus === 'syncing' ? 'Sync...' : syncStatus === 'ok' ? 'Sync OK' : syncStatus === 'error' ? 'Sync ERR' : 'Sync'}
          </button>
        </div>
      </div>
      {/* Liste events */}
      <div
        ref={listRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: 11,
          lineHeight: 1.4,
        }}
      >
        {filtered.length === 0 && (
          <div style={{ color: '#555', padding: '8px 0', fontSize: 11 }}>
            Aucun event. Activer MB_DEBUG dans localStorage.
          </div>
        )}
        {filtered.map((ev, i) => {
          const canExpand = ev.payload !== undefined
          const isExpanded = canExpand && expandedIndices.has(i)
          return (
            <div
              key={i}
              style={{
                borderBottom: '1px solid #1a1a1a',
              }}
            >
              <div
                onClick={canExpand ? () => toggleExpand(i) : undefined}
                style={{
                  display: 'flex',
                  gap: 6,
                  padding: '1px 0',
                  cursor: canExpand ? 'pointer' : 'default',
                }}
                title={canExpand ? (isExpanded ? 'Replier' : 'Deplier le payload JSON complet') : undefined}
              >
                <span style={{ color: '#555', flexShrink: 0, width: 10 }}>
                  {canExpand ? (isExpanded ? '▾' : '▸') : ''}
                </span>
                <span style={{ color: '#555', flexShrink: 0 }}>{ev.ts}</span>
                <span
                  style={{
                    color: SOURCE_COLORS[ev.source],
                    fontWeight: 700,
                    flexShrink: 0,
                    minWidth: 52,
                  }}
                >
                  [{ev.source}]
                </span>
                <span style={{ color: '#ddd', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ev.label}
                </span>
                {canExpand && !isExpanded && (
                  <span style={{ color: '#888', flexShrink: 0, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {JSON.stringify(ev.payload).slice(0, 60)}
                  </span>
                )}
              </div>
              {isExpanded && (
                <pre
                  style={{
                    margin: '0 0 4px 16px',
                    padding: '6px 8px',
                    background: '#1a1a1a',
                    border: '1px solid #2a2a2a',
                    borderRadius: 3,
                    color: '#ddd',
                    fontSize: 11,
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(ev.payload, null, 2)}
                </pre>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// --- Sous-composant Cache Tab ---

function CacheTab() {
  const [snapshot, setSnapshot] = useState<{ key: string; status: string; count: number | null; updatedAt: string | null }[]>([])

  const refresh = useCallback(() => {
    // Le singleton queryClient (partagé par QueryClientProvider ET trpc.Provider dans
    // AppProviders) porte directement le cache — pas besoin de fouiller le proxy
    // trpc.useUtils() (en tRPC v11 c'est un DecoratedProcedureUtilsRecord : toute
    // propriété inconnue comme .client ou .queryClient résout un sous-proxy de router
    // au lieu de renvoyer undefined, d'où le crash "... is not a function").
    const all = queryClient.getQueryCache().getAll() as Array<{
      queryKey: unknown[]
      state: { status: string; dataUpdatedAt: number; data: unknown }
    }>
    const snap = all.map(q => {
      const key = JSON.stringify(q.queryKey).slice(0, 80)
      const data = q.state.data
      const count = Array.isArray(data) ? data.length : null
      const updatedAt = q.state.dataUpdatedAt
        ? new Date(q.state.dataUpdatedAt).toISOString().slice(11, 23)
        : null
      return { key, status: q.state.status, count, updatedAt }
    })
    setSnapshot(snap)
  }, [])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, 2000)
    return () => clearInterval(interval)
  }, [refresh])

  return (
    <div style={{ fontFamily: 'monospace', fontSize: 11 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button onClick={refresh} style={btnStyle}>Refresh</button>
      </div>
      {snapshot.length === 0 && <div style={{ color: '#555' }}>Aucune query en cache.</div>}
      {snapshot.map((q, i) => (
        <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid #1a1a1a' }}>
          <span style={{ color: q.status === 'success' ? '#4CAF50' : q.status === 'error' ? '#F44336' : '#FF9800', fontWeight: 700, marginRight: 6 }}>
            [{q.status}]
          </span>
          <span style={{ color: '#ddd' }}>{q.key}</span>
          {q.count !== null && <span style={{ color: '#888', marginLeft: 6 }}>{q.count} items</span>}
          {q.updatedAt && <span style={{ color: '#555', marginLeft: 6 }}>{q.updatedAt}</span>}
        </div>
      ))}
    </div>
  )
}

// --- Sous-composant Stores Tab ---

function StoresTab() {
  const messageSessions = useMessagesStore(s => s.sessions)
  const statusSessions = useStatusStore(s => s.statuses)
  const metricsSessions = useMetricsStore(s => s.sessions)
  const terminalSessions = useTerminalStore(s => s.sessions)
  const terminalMode = useUIStore(s => s.terminalMode)
  const tabs = useTabsStore(s => s.tabs)
  const activeTabId = useTabsStore(s => s.activeTabId)
  const user = useAuthStore(s => s.user)

  const activeTab = tabs.find(t => t.id === activeTabId)
  const sid = activeTab?.sessionId
  const activeMessage = sid ? messageSessions[sid] : null
  const activeStatus = sid ? (statusSessions[sid] ?? 'idle') : null
  const activeMetrics = sid ? metricsSessions[sid] : null
  const activeTerminal = sid ? terminalSessions[sid] : null

  return (
    <div style={{ fontFamily: 'monospace', fontSize: 11 }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ color: '#FF9800', fontWeight: 700, marginBottom: 3 }}>Tabs</div>
        <div style={{ color: '#888' }}>count: {tabs.length} | activeTabId: {activeTabId?.slice(0, 12) ?? 'null'}</div>
        {activeTab && (
          <div style={{ color: '#ddd', marginTop: 2 }}>
            active: {activeTab.title} (sid: {activeTab.sessionId?.slice(0, 12) ?? 'none'})
          </div>
        )}
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ color: '#FF9800', fontWeight: 700, marginBottom: 3 }}>Messages Store</div>
        <div style={{ color: '#888' }}>sessions: {Object.keys(messageSessions).length} | terminalMode: {terminalMode ? 'true' : 'false'}</div>
        {activeMessage && (
          <div style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #333' }}>
            <div style={{ color: '#ddd' }}>status: <span style={{ color: '#4CAF50' }}>{activeStatus ?? 'unknown'}</span></div>
            <div style={{ color: '#ddd' }}>isStreaming: <span style={{ color: activeMessage.isStreaming ? '#FF9800' : '#555' }}>{String(activeMessage.isStreaming)}</span></div>
            <div style={{ color: '#ddd' }}>streamingContent: {activeMessage.streamingContent.length} chars</div>
            <div style={{ color: '#ddd' }}>liveTerminalEvents: {activeTerminal?.liveEvents.length ?? 0}</div>
            <div style={{ color: '#ddd' }}>model: {activeMetrics?.model ?? 'null'}</div>
            <div style={{ color: '#ddd' }}>totalCost: ${(activeMetrics?.totalCost ?? 0).toFixed(4)}</div>
          </div>
        )}
      </div>
      <div>
        <div style={{ color: '#FF9800', fontWeight: 700, marginBottom: 3 }}>Auth Store</div>
        <div style={{ color: '#888' }}>user: {user?.email ?? 'null'} | role: {user?.role ?? 'null'}</div>
      </div>
    </div>
  )
}

// --- Sous-composant Daemon Tab ---

function DaemonTab() {
  const tabs = useTabsStore(s => s.tabs)
  const activeTabId = useTabsStore(s => s.activeTabId)
  const messageSessions = useMessagesStore(s => s.sessions)
  const statusSessions = useStatusStore(s => s.statuses)
  const metricsSessions = useMetricsStore(s => s.sessions)
  const accessToken = useAuthStore(s => s.accessToken)

  const activeTab = tabs.find(t => t.id === activeTabId)
  const sessionId = activeTab?.sessionId ?? null
  const messageState = sessionId ? messageSessions[sessionId] : null
  const agentStatus = sessionId ? (statusSessions[sessionId] ?? 'idle') : null
  const metricsState = sessionId ? metricsSessions[sessionId] : null

  const { data: contextUsage } = trpc.sessions.contextUsage.useQuery(
    { sessionId: sessionId! },
    { enabled: !!sessionId && !!accessToken, refetchInterval: 5000 },
  )

  return (
    <div style={{ fontFamily: 'monospace', fontSize: 11 }}>
      <div style={{ color: '#2196F3', fontWeight: 700, marginBottom: 6 }}>Session active</div>
      {!sessionId && <div style={{ color: '#555' }}>Aucune session active.</div>}
      {sessionId && (
        <div style={{ paddingLeft: 8, borderLeft: '2px solid #333' }}>
          <div style={{ color: '#888' }}>sessionId: {sessionId.slice(0, 20)}...</div>
          <div style={{ color: '#ddd', marginTop: 4 }}>
            status: <span style={{ color: '#4CAF50' }}>{agentStatus ?? 'unknown'}</span>
          </div>
          <div style={{ color: '#ddd' }}>
            isStreaming: <span style={{ color: messageState?.isStreaming ? '#FF9800' : '#555' }}>{String(messageState?.isStreaming ?? false)}</span>
          </div>
          <div style={{ color: '#ddd' }}>model: {metricsState?.model ?? 'null'}</div>
          <div style={{ color: '#ddd' }}>cost: ${(metricsState?.totalCost ?? 0).toFixed(4)}</div>
          {contextUsage && (
            <>
              <div style={{ color: '#ddd', marginTop: 4 }}>
                context: <span style={{ color: contextUsage.percentage > 80 ? '#F44336' : '#4CAF50' }}>{contextUsage.percentage.toFixed(1)}%</span>
              </div>
              <div style={{ color: '#888' }}>tokens: {contextUsage.totalTokens.toLocaleString()} / {contextUsage.maxTokens.toLocaleString()}</div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// --- Style helper ---

const btnStyle: React.CSSProperties = {
  padding: '2px 8px',
  background: '#2a2a2a',
  border: '1px solid #444',
  borderRadius: 3,
  color: '#ccc',
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'monospace',
}

// --- Composant principal ---

export function DebugPanel() {
  const { isOpen, debugEnabled, panelHeight, toggle, close, toggleDebug, setPanelHeight } = useDebugPanelStore()
  const [activeTab, setActiveTab] = useState<Tab>('stream')
  const resizing = useRef(false)

  // sessionId de l'onglet actif — ancre que Fabrice donne à Claude pour tracer
  // côté serveur/daemon. Copié en entier (jamais tronqué).
  const openTabs = useTabsStore(s => s.tabs)
  const activeTabId = useTabsStore(s => s.activeTabId)
  const sessionId = openTabs.find(t => t.id === activeTabId)?.sessionId ?? null
  const [sidCopied, setSidCopied] = useState(false)

  const copySessionId = useCallback(() => {
    if (!sessionId) return
    void navigator.clipboard.writeText(sessionId)
    setSidCopied(true)
    setTimeout(() => setSidCopied(false), 1500)
  }, [sessionId])

  // Toggle Ctrl+Shift+D
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggle])

  // Redimensionnement par glisser depuis la poignee en haut du panneau.
  // Hauteur = distance entre le bas de la fenetre et la position de la souris.
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    resizing.current = true
    const onMove = (moveEvent: MouseEvent) => {
      if (!resizing.current) return
      // Garde au moins ~120px de hauteur d'app visible au-dessus (TopBar + de quoi lire/taper)
      const maxUsable = Math.max(150, window.innerHeight - 120)
      setPanelHeight(Math.min(maxUsable, window.innerHeight - moveEvent.clientY))
    }
    const onUp = () => {
      resizing.current = false
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [setPanelHeight])

  if (!isOpen) return null

  const tabs: { id: Tab; label: string }[] = [
    { id: 'stream', label: 'Stream' },
    { id: 'cache', label: 'Cache' },
    { id: 'stores', label: 'Stores' },
    { id: 'daemon', label: 'Daemon' },
  ]

  // Filet de securite : une hauteur persistee sur un grand ecran peut depasser
  // un ecran plus petit (autre appareil, fenetre redimensionnee) — on garde
  // toujours au moins ~120px d'app visible au-dessus, peu importe panelHeight stocke.
  const effectiveHeight = typeof window !== 'undefined'
    ? Math.min(panelHeight, Math.max(150, window.innerHeight - 120))
    : panelHeight

  return (
    <div
      style={{
        // Docke en flux normal (App.tsx) — pousse l'app vers le haut, ne la masque pas.
        height: effectiveHeight,
        flexShrink: 0,
        background: '#111',
        borderTop: '2px solid #333',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'monospace',
        fontSize: 12,
        color: '#ccc',
      }}
    >
      {/* Poignee de redimensionnement */}
      <div
        onMouseDown={handleResizeStart}
        style={{
          height: 5,
          flexShrink: 0,
          cursor: 'ns-resize',
          background: '#222',
        }}
        title="Glisser pour redimensionner"
      />
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px 10px',
          background: '#1a1a1a',
          borderBottom: '1px solid #333',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <span style={{ color: '#FF9800', fontWeight: 700, fontSize: 12 }}>MB Debug</span>
        {/* Copier le sessionId de l'onglet actif (ancre de trace serveur/daemon) */}
        <button
          onClick={copySessionId}
          disabled={!sessionId}
          style={{
            ...btnStyle,
            color: sidCopied ? '#4CAF50' : sessionId ? '#ccc' : '#666',
            cursor: sessionId ? 'pointer' : 'default',
            opacity: sessionId ? 1 : 0.5,
          }}
          title={sessionId ? `Copier le sessionId (${sessionId})` : 'Aucune session active'}
        >
          {sidCopied ? 'sid ✓' : `sid ${sessionId ? sessionId.slice(0, 8) : '—'}`}
        </button>
        {/* Onglets */}
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '2px 10px',
              background: activeTab === tab.id ? '#333' : 'transparent',
              border: activeTab === tab.id ? '1px solid #555' : '1px solid transparent',
              borderRadius: 3,
              color: activeTab === tab.id ? '#fff' : '#888',
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: 'monospace',
            }}
          >
            {tab.label}
          </button>
        ))}
        <button
          onClick={toggleDebug}
          style={{
            ...btnStyle,
            color: debugEnabled ? '#4CAF50' : '#888',
            borderColor: debugEnabled ? '#4CAF50' : '#444',
            fontWeight: 700,
          }}
          title={debugEnabled ? 'Désactiver le debug (MB_DEBUG)' : 'Activer le debug (MB_DEBUG)'}
        >
          debug: {debugEnabled ? 'ON' : 'OFF'}
        </button>
        <button
          onClick={close}
          style={{ ...btnStyle, marginLeft: 'auto', padding: '1px 8px' }}
          title="Fermer (Ctrl+Shift+D)"
        >
          ×
        </button>
      </div>

      {/* Contenu onglet */}
      <div style={{ flex: 1, overflow: 'hidden', padding: '6px 10px' }}>
        {activeTab === 'stream' && <StreamTab />}
        {activeTab === 'cache' && (
          <div style={{ height: '100%', overflowY: 'auto' }}>
            <CacheTab />
          </div>
        )}
        {activeTab === 'stores' && (
          <div style={{ height: '100%', overflowY: 'auto' }}>
            <StoresTab />
          </div>
        )}
        {activeTab === 'daemon' && (
          <div style={{ height: '100%', overflowY: 'auto' }}>
            <DaemonTab />
          </div>
        )}
      </div>
    </div>
  )
}
